// Video container/MIME handling shared by the upload, storage, and share paths.
//
// Uploads used to be labelled `video/mp4` and stored as `{id}.mp4` regardless of
// what was actually uploaded. QuickTime (.mov) files — what iPhones and most Macs
// produce — were therefore served with a MIME type that does not match their bytes,
// which Safari refuses to play. Resolve the real extension/MIME per file instead.

import { sniffVideoContentType, VIDEO_SNIFF_BYTE_COUNT } from './videoContainerSniffer';

export const DEFAULT_VIDEO_EXTENSION = 'mp4';
export const DEFAULT_VIDEO_CONTENT_TYPE = 'video/mp4';

const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  qt: 'video/quicktime',
  webm: 'video/webm',
  ogg: 'video/ogg',
  ogv: 'video/ogg',
  mkv: 'video/x-matroska',
  avi: 'video/x-msvideo',
  wmv: 'video/x-ms-wmv',
  flv: 'video/x-flv',
  mpeg: 'video/mpeg',
  mpg: 'video/mpeg',
  '3gp': 'video/3gpp',
  '3g2': 'video/3gpp2',
  ts: 'video/mp2t',
  mts: 'video/mp2t',
  m2ts: 'video/mp2t'
};

const CONTENT_TYPE_EXTENSIONS: Record<string, string> = {
  'video/mp4': 'mp4',
  'video/x-m4v': 'm4v',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
  'video/ogg': 'ogv',
  'video/x-matroska': 'mkv',
  'video/x-msvideo': 'avi',
  'video/x-ms-wmv': 'wmv',
  'video/x-flv': 'flv',
  'video/mpeg': 'mpg',
  'video/3gpp': '3gp',
  'video/3gpp2': '3g2',
  'video/mp2t': 'ts'
};

/**
 * Containers every current browser can open. Only these are offered in the file
 * picker — the rest are still accepted if a user picks one, since the guard below
 * is permissive, but we do not advertise formats that usually fail to decode.
 */
const PLAYABLE_VIDEO_EXTENSIONS = ['mp4', 'm4v', 'mov', 'qt', 'webm', 'ogg', 'ogv', '3gp'];

/**
 * Containers no mainstream browser decodes. Accepting them and explaining the
 * failure beats silently rejecting a file the user believes is a normal video.
 */
const UNPLAYABLE_CONTAINER_LABELS: Record<string, string> = {
  avi: 'AVI',
  wmv: 'Windows Media (WMV)',
  flv: 'Flash Video (FLV)',
  mkv: 'Matroska (MKV)',
  mpeg: 'MPEG program stream',
  mpg: 'MPEG program stream',
  ts: 'MPEG transport stream',
  mts: 'AVCHD',
  m2ts: 'AVCHD'
};

/** Every extension we know how to label, playable or not. */
export const SUPPORTED_VIDEO_EXTENSIONS = Object.keys(EXTENSION_CONTENT_TYPES);

/** `accept` value for the video file input. */
export const VIDEO_INPUT_ACCEPT = `video/*,${PLAYABLE_VIDEO_EXTENSIONS.map(ext => `.${ext}`).join(',')}`;

/** Lowercased extension without the dot, or '' when the name has none. */
export const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === fileName.length - 1) return '';
  return fileName.slice(lastDot + 1).toLowerCase();
};

/**
 * A file counts as video when the browser says so, or when we recognise the
 * extension. Some browsers report an empty `type` for .mov, so both matter.
 */
export const isSupportedVideoFile = (file: File): boolean => (
  file.type.startsWith('video/') || SUPPORTED_VIDEO_EXTENSIONS.includes(getFileExtension(file.name))
);

/**
 * Why a video the app accepted failed to play, keyed off its container. Every
 * branch ends in the same practical advice — convert to H.264 MP4 — but naming
 * the actual reason beats a blanket "corrupt or unsupported format".
 */
export const describeVideoLoadFailure = (fileName: string): string => {
  const extension = getFileExtension(fileName);

  // QuickTime opens everywhere; its codec often does not. iPhone "High
  // Efficiency" (HEVC) and ProRes decode in Safari but not Chrome or Firefox.
  if (extension === 'mov' || extension === 'qt') {
    return (
      'This browser cannot decode this .mov file — QuickTime videos recorded in ' +
      'High Efficiency (HEVC) or ProRes only play in Safari.\n\n' +
      'Try opening Croop in Safari, or convert the video to H.264 MP4.'
    );
  }

  const unplayableLabel = UNPLAYABLE_CONTAINER_LABELS[extension];
  if (unplayableLabel) {
    return (
      `${unplayableLabel} files cannot be played by web browsers, whatever is inside them.\n\n` +
      'Please convert the video to MP4 and upload it again.'
    );
  }

  return 'The file may be corrupt or in an unsupported format. Please try another video file.';
};

/**
 * MIME type to store the upload under, from the filename and browser-reported
 * type alone. `resolveVideoUploadType` is preferred where the bytes can be read.
 */
export const resolveVideoContentType = (file: File): string => {
  const reported = file.type.toLowerCase();
  if (CONTENT_TYPE_EXTENSIONS[reported]) return reported;
  if (reported.startsWith('video/')) return reported;

  return EXTENSION_CONTENT_TYPES[getFileExtension(file.name)] || DEFAULT_VIDEO_CONTENT_TYPE;
};

/**
 * Extension for the Storage object name. Prefer the uploaded file's own
 * extension so the object name matches its bytes; otherwise derive it from the
 * content type.
 */
export const resolveVideoStorageExtension = (file: File): string => {
  const extension = getFileExtension(file.name);
  if (SUPPORTED_VIDEO_EXTENSIONS.includes(extension)) return extension;

  return CONTENT_TYPE_EXTENSIONS[file.type.toLowerCase()] || DEFAULT_VIDEO_EXTENSION;
};

export const extensionForVideoContentType = (contentType: string): string => (
  CONTENT_TYPE_EXTENSIONS[contentType.toLowerCase()] || DEFAULT_VIDEO_EXTENSION
);

/**
 * Reads the head of a file for container detection. `Blob.arrayBuffer` is missing
 * in Safari 13 — the very browser .mov users are most likely to be on — so fall
 * back to FileReader rather than give up on sniffing there.
 */
const readHeadBytes = async (file: File): Promise<Uint8Array | null> => {
  if (typeof file.slice !== 'function') return null;
  const head = file.slice(0, VIDEO_SNIFF_BYTE_COUNT);

  if (typeof head.arrayBuffer === 'function') {
    return new Uint8Array(await head.arrayBuffer());
  }

  if (typeof FileReader === 'undefined') return null;

  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(
      reader.result instanceof ArrayBuffer ? new Uint8Array(reader.result) : null
    );
    reader.onerror = () => resolve(null);
    reader.readAsArrayBuffer(head);
  });
};

/**
 * Content type and storage extension for an upload, preferring what the file's
 * own bytes say. A .mov renamed to .mp4 (common, and invisible to the filename
 * and browser MIME type) is stored as QuickTime and stays playable in Safari.
 * Falls back to the filename when the header is unreadable or unrecognised.
 */
export const resolveVideoUploadType = async (
  file: File
): Promise<{ contentType: string; extension: string }> => {
  const fallback = {
    contentType: resolveVideoContentType(file),
    extension: resolveVideoStorageExtension(file)
  };

  try {
    const head = await readHeadBytes(file);
    if (!head) return fallback;

    const sniffed = sniffVideoContentType(head);
    if (!sniffed) return fallback;

    // Trust the filename's extension when it already agrees with the bytes:
    // .m4v and .ogv both sniff to a type whose canonical extension differs.
    const extension = resolveVideoContentType(file) === sniffed
      ? fallback.extension
      : extensionForVideoContentType(sniffed);

    return { contentType: sniffed, extension };
  } catch {
    // Unreadable header (permissions, empty slice, jsdom): the filename is enough.
    return fallback;
  }
};
